import { API, API_BASE } from '../../config/api';
import { useState } from 'react';
import { api } from '../../context/AuthContext';
import { HomeFeatureStar } from './HomeFeatureStar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MoreVertical, Video, Star } from 'lucide-react';


function getEmbedUrl(url) {
  if (!url) return '';
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

function getThumbnail(url) {
  if (!url) return '';
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
  return '';
}

export function VideosTab({ videos, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', url: '', description: '', is_featured: false });

  const resetForm = () => {
    setForm({ title: '', url: '', description: '', is_featured: false });
    setEditing(null);
  };

  const openEdit = (video) => {
    setForm({
      title: video.title || '', url: video.url || '',
      description: video.description || '', is_featured: video.is_featured || false
    });
    setEditing(video);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!getEmbedUrl(form.url)) {
      toast.error('Please enter a valid YouTube or Vimeo URL');
      return;
    }
    try {
      if (editing) {
        await api.put(`${API}/videos/${editing.id}`, form);
        toast.success('Video updated!');
      } else {
        await api.post(`${API}/videos`, form);
        toast.success('Video added!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      { const detail = err.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to save video'); }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`${API}/videos/${id}`);
      toast.success('Video deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const setFeatured = async (id) => {
    try {
      await api.put(`${API}/videos/${id}`, { is_featured: true });
      toast.success('Featured video updated!');
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6" data-testid="videos-content">
      <div className="flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">MUSIC VIDEOS</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="add-video-btn">
              <Plus className="w-4 h-4 mr-2" /> ADD VIDEO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-black uppercase">
                {editing ? 'EDIT VIDEO' : 'ADD MUSIC VIDEO'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="overline">Title</Label>
                <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required data-testid="video-title-input" />
              </div>
              <div>
                <Label className="overline">YouTube or Vimeo URL</Label>
                <Input value={form.url} onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required
                  placeholder="https://youtube.com/watch?v=..." data-testid="video-url-input" />
                {form.url && getEmbedUrl(form.url) && (
                  <div className="mt-3 aspect-video bg-black border border-[#262626] overflow-hidden">
                    <iframe src={getEmbedUrl(form.url)} title="Preview" className="w-full h-full" allowFullScreen />
                  </div>
                )}
              </div>
              <div>
                <Label className="overline">Description (optional)</Label>
                <textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={2}
                  data-testid="video-description-input" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_featured} onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_featured: checked }))} data-testid="video-featured-switch" />
                <Label className="overline">Featured Video (displayed large on homepage)</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="video-submit-btn">
                  {editing ? 'UPDATE' : 'ADD VIDEO'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-[#262626]">CANCEL</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {videos.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Video className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No videos yet. Add your first music video!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map(video => (
            <div key={video.id} className={`bg-[#141414] border overflow-hidden ${video.is_featured ? 'border-[#FF3B30]' : 'border-[#262626]'}`} data-testid={`video-item-${video.id}`}>
              <div className="aspect-video bg-black overflow-hidden relative">
                {getThumbnail(video.url) ? (
                  <img src={getThumbnail(video.url)} alt={video.title} className="w-full h-full object-cover" />
                ) : (
                  <iframe src={getEmbedUrl(video.url)} title={video.title} className="w-full h-full" />
                )}
                {video.is_featured && (
                  <span className="absolute top-2 left-2 bg-[#FF3B30] px-2 py-0.5 text-xs font-mono uppercase flex items-center gap-1">
                    <Star className="w-3 h-3" /> Featured
                  </span>
                )}
                <div className="absolute top-2 right-2">
                  <HomeFeatureStar item={video} endpoint="videos" className="bg-black/60 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur" onToggled={(featured, count) => {
                    setVideos(arr => arr.map(v => v.id === video.id
                      ? { ...v, home_featured: featured, home_featured_order: featured ? count - 1 : undefined }
                      : v));
                  }} />
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold">{video.title}</h3>
                    {video.description && <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-2">{video.description}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    {!video.is_featured && (
                      <button onClick={() => setFeatured(video.id)}
                        className="px-3 py-1.5 text-xs font-bold uppercase bg-[#DAA520] hover:bg-[#DAA520]/80 text-black transition-all"
                        data-testid={`video-feature-${video.id}`}>
                        <Star className="w-3.5 h-3.5 inline mr-1" />FEATURE
                      </button>
                    )}
                    <button onClick={() => openEdit(video)}
                      className="px-3 py-1.5 text-xs font-bold uppercase bg-[#00B4D8] hover:bg-[#00B4D8]/80 text-black transition-all"
                      data-testid={`video-edit-${video.id}`}>
                      <Edit className="w-3.5 h-3.5 inline mr-1" />EDIT
                    </button>
                    <button onClick={() => handleDelete(video.id)}
                      className="px-3 py-1.5 text-xs font-bold uppercase bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white transition-all"
                      data-testid={`video-delete-${video.id}`}>
                      <Trash2 className="w-3.5 h-3.5 inline mr-1" />DEL
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
